import React, { useState, useEffect, useMemo, useRef } from 'react';
import dayjs from 'dayjs';
import {
    Card,
    Tag,
    Table,
    Tabs,
    Typography,
    Modal,
    Form,
    Button,
    message,
    Upload,
    Divider,
    Select,
    Space, Tooltip
} from 'antd';
import {
    UserAddOutlined,
    EyeOutlined,
    EditOutlined,
    TruckFilled,
    PlusOutlined,
    EnvironmentOutlined
} from '@ant-design/icons';
import { EquipmentFilterBar, UserFilterBar } from '../components/DashboardFilterBar';
import apiClient from '../helpers/apiHelper';
import { ENDPOINTS, REQUEST_TYPES, MESSAGES } from '../helpers/apiConfig';
import { EQUIPMENT_BASE_COLUMNS, USER_BASE_COLUMNS, ROLES } from '../constants';
import { EquipmentForm, UserForm } from "../components/Form";
import EquipmentDetailView from "../components/EquipmentDetailView";

const { Title } = Typography;

export const DashboardPage = ({ user }) => {
    const [activeTab, setActiveTab] = useState('all equipments');
    const [users, setUsers] = useState([]);
    const [equipments, setEquipments] = useState([]);
    const [userLoading, setUserLoading] = useState(true);
    const [equipLoading, setEquipLoading] = useState(true);
    const [equipSubmitLoading, setEquipSubmitLoading] = useState(false);
    const [userSubmitLoading, setUserSubmitLoading] = useState(false);
    const [equipUpdateLoading, setEquipUpdateLoading] = useState(false);
    const [userUpdateLoading, setUserUpdateLoading] = useState(false);
    const [liveLocationLoading, setLiveLocationLoading] = useState({});
    const [userVersion, setUserVersion] = useState(0);
    const [allEquipsFilters, setAllEquipsFilters] = useState({ types: [], vehicles: [], owners: [], plantUsers: [], plantHods: [] });
    const [allUsersFilters, setAllUsersFilters] = useState({ email: [], role: [], plantHods: [] });
    const [addEquipModalOpen, setAddEquipModalOpen] = useState(false);
    const [addUserModalOpen, setAddUserModalOpen] = useState(false);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [updateEquipModalOpen, setUpdateEquipModalOpen] = useState(false);
    const [updateUserModalOpen, setUpdateUserModalOpen] = useState(false);
    const [selectedEquipment, setSelectedEquipment] = useState(null);
    const [activeDraftId, setActiveDraftId] = useState(null);
    const [drafts, setDrafts] = useState(() => {
        try { return JSON.parse(localStorage.getItem('equipmentDrafts') || '[]'); }
        catch { return []; }
    });
    
    const addEquipScrollRef = useRef(null);
    const [addEquipForm] = Form.useForm();
    const [updateEquipForm] = Form.useForm();
    const [addUserForm] = Form.useForm();
    const [updateUserForm] = Form.useForm();
    const showAddEquipModal = () => {
        addEquipForm.resetFields();
        if (user?.role === ROLES.PLANT_USER) {
            addEquipForm.setFieldsValue({ plant_user: user?.email, plant_hod: user?.plant_hod });
        }
        setAddEquipModalOpen(true);
    };
    const showAddUserModal = () => setAddUserModalOpen(true);

    const handleSaveEquipDraft = () => {
        const values = addEquipForm.getFieldsValue();
        const newDraft = {
            id: Date.now(),
            savedAt: new Date().toLocaleString(),
            label: values.equipment_name || values.vehicle_no || `Draft ${drafts.length + 1}`,
            values,
        };
        const updated = [...drafts, newDraft];
        setDrafts(updated);
        localStorage.setItem('equipmentDrafts', JSON.stringify(updated));
        addEquipForm.resetFields();
        if (user?.role === ROLES.PLANT_USER) {
            addEquipForm.setFieldsValue({ plant_user: user?.email, plant_hod: user?.plant_hod });
        }
        setAddEquipModalOpen(false);
        message.success("Draft saved successfully");
    };

    const handleLoadDraft = (draftId) => {
        const draft = drafts.find(d => d.id === draftId);
        if (draft) {
            addEquipForm.setFieldsValue(draft.values);
            setActiveDraftId(draftId);
            setAddEquipModalOpen(true);
            resetAddEquipScroll();
        }
    };

    const handleDeleteDraft = (draftId) => {
        const updated = drafts.filter(d => d.id !== draftId);
        setDrafts(updated);
        localStorage.setItem('equipmentDrafts', JSON.stringify(updated));
    };

    const userId = user?.email;
    const userRole = user?.role;

    const fetchEquipments = async () => {
        setEquipLoading(true);

        try {
            const res = await apiClient(ENDPOINTS.EQUIPMENT.FETCH_EQUIPMENTS, {
                method: 'POST',
                body: JSON.stringify({
                    request_type: REQUEST_TYPES.FETCH_EQUIPMENTS
                })
            });

            const equipmentList = res?.items || [];
            setEquipments(equipmentList);
        } catch (error) {
            message.error(MESSAGES.ERROR.FETCH_EQUIPMENTS);
        } finally {
            setEquipLoading(false);
        }
    };

    const fetchUsers = async () => {
        setUserLoading(true);

        try {
            const res = await apiClient(ENDPOINTS.USER.FETCH_USERS, {
                method: 'POST',
                body: JSON.stringify({
                    request_type: REQUEST_TYPES.FETCH_USERS
                })
            });

            const userList = res?.items || [];
            setUsers(userList);

        } catch (error) {
            message.error(MESSAGES.ERROR.FETCH_USERS);
        } finally {
            setUserLoading(false);
        }
    };

    const handleView = (record) => {
        setSelectedEquipment(record);
        setViewModalOpen(true);
    };

    const handleLiveLocation = async (record) => {
        const vehicleNo = (record?.vehicle_no || '').trim();
        if (!vehicleNo) {
            message.error(MESSAGES.ERROR.LIVE_LOCATION_NO_VEHICLE);
            return;
        }
        const mapsTab = window.open('', '_blank');
        if (mapsTab) {
            mapsTab.opener = null;
        }
        setLiveLocationLoading((prev) => ({ ...prev, [vehicleNo]: true }));

        try {
            const res = await apiClient(ENDPOINTS.EQUIPMENT.FETCH_LIVE_LOCATION, {
                method: 'POST',
                body: JSON.stringify({
                    request_type: REQUEST_TYPES.FETCH_LIVE_LOCATION,
                    vehicle_no: vehicleNo,
                }),
            });

            const mapsUrl = res?.maps_url;
            if (!mapsUrl) {
                if (mapsTab) {
                    mapsTab.close();
                }
                message.error(MESSAGES.ERROR.FETCH_LIVE_LOCATION);
                return;
            }

            if (mapsTab) {
                mapsTab.location.href = mapsUrl;
            } else {
                // Popup was blocked; fall back to navigating the current tab.
                window.location.href = mapsUrl;
            }
            message.success(MESSAGES.SUCCESS.FETCH_LIVE_LOCATION);
        } catch (error) {
            if (mapsTab) {
                mapsTab.close();
            }
            message.error(error?.message || MESSAGES.ERROR.FETCH_LIVE_LOCATION);
        } finally {
            setLiveLocationLoading((prev) => ({ ...prev, [vehicleNo]: false }));
        }
    };

    const handleUpdateEquipValue = (record) => {
        setSelectedEquipment(record);
        updateEquipForm.setFieldsValue({
            equipment_type: record.equipment_type,
            equipment_name: record.equipment_name,
            vehicle_no: record.vehicle_no,
            gps_imei_no: record.gps_imei_no,
            fuel_type: record.fuel_type,
            hiring_purpose: record.hiring_purpose,
            manufacturer: record.manufacturer,
            manufacturing_year: record.manufacturing_year,
            plant_name: record.plant_name,
            deployment_area: record.deployment_area,
            plant_user: record.plant_user,
            plant_hod: record.plant_hod,
            driver_mobile_no: record.driver_mobile_no,
            owner_email: record.owner_email,
            amns_wo: record.amns_wo,
            line_item_no: record.line_item_no,
            cost_center: record.cost_center,
            billing_cycle: record.billing_cycle,
            unit_of_measure: record.unit_of_measure,
            contract_rate: record.contract_rate,
            time_range: record.service_start_time && record.service_end_time
                ? [dayjs(record.service_start_time, 'HH:mm:ss'), dayjs(record.service_end_time, 'HH:mm:ss')]
                : undefined,
            date_range: record.service_start_date && record.service_end_date
                ? [dayjs(record.service_start_date), dayjs(record.service_end_date)]
                : undefined,
            additional_remarks: record.additional_remarks,
        });
        setUpdateEquipModalOpen(true);
    };

    const handleUpdateUserValue = (record) => {
        updateUserForm.setFieldsValue({
            full_name: record.full_name,
            email: record.email,
            role: record.role,
            plant_hod: record.plant_hod
        });
        setUpdateUserModalOpen(true);
    }

    const buildEquipmentPayload = (values) => {
        const [serviceStartDate, serviceEndDate] = values.date_range || [];
        const [serviceStartTime, serviceEndTime] = values.time_range || [];
        return {
            equipment_type: values.equipment_type,
            equipment_name: values.equipment_name,
            fuel_type: values.fuel_type,
            hiring_purpose: values.hiring_purpose,
            manufacturer: values.manufacturer,
            manufacturing_year: values.manufacturing_year,
            vehicle_no: values.vehicle_no,
            deployment_area: values.deployment_area,
            plant_name: values.plant_name,
            plant_user: values.plant_user,
            plant_hod: values.plant_hod,
            cost_center: values.cost_center,
            owner_email: values.owner_email,
            driver_mobile_no: values.driver_mobile_no,
            amns_wo: values.amns_wo,
            line_item_no: values.line_item_no,
            billing_cycle: values.billing_cycle,
            unit_of_measure: values.unit_of_measure,
            contract_rate: values.contract_rate,
            service_start_date: serviceStartDate ? serviceStartDate.format('YYYY-MM-DD') : '',
            service_end_date: serviceEndDate ? serviceEndDate.format('YYYY-MM-DD') : '',
            service_start_time: serviceStartTime ? serviceStartTime.format('HH:mm:ss') : '',
            service_end_time: serviceEndTime ? serviceEndTime.format('HH:mm:ss') : '',
            additional_remarks: values.additional_remarks,
        };
    };

    const handleEquipSubmit = async (values) => {
        try {
            setEquipSubmitLoading(true);

            const payload = {
                ...buildEquipmentPayload(values),
                request_type: REQUEST_TYPES.ADD_EQUIPMENT,
                created_by: userId,
            };

            await apiClient(ENDPOINTS.EQUIPMENT.ADD_EQUIPMENT, {
                method: "POST",
                body: JSON.stringify(payload),
            });
            setAddEquipModalOpen(false);
            addEquipForm.resetFields();
            if (activeDraftId) {
                handleDeleteDraft(activeDraftId);
                setActiveDraftId(null);
            }
            if (userRole === ROLES.PLANT_USER) {
                addEquipForm.setFieldsValue({ plant_user: user?.email, plant_hod: user?.plant_hod });
            }
            message.success(MESSAGES.SUCCESS.ADD_EQUIPMENT);
            fetchEquipments();

        } catch (error) {
            if (error.errorFields) {
                message.error("Please fill in all required fields.");
            } else {
                message.error(error?.message);
            }
        } finally {
            setEquipSubmitLoading(false);
        }
    };

    const handleUserSubmit = async () => {
        try {
            const values = addUserForm.getFieldsValue();
            const role = userRole === ROLES.SPEND_TEAM ? values.role : userRole === ROLES.PLANT_HOD ? ROLES.PLANT_USER : ROLES.OWNER;
            const plant_hod = addUserForm.getFieldValue('plant_hod');

            // Extra safety check for Plant user role
            if (role === ROLES.PLANT_USER && !plant_hod) {
                message.error("Please select a Plant HOD");
                return;
            }

            setUserSubmitLoading(true);

            const payload = {
                request_type: REQUEST_TYPES.ADD_USER,
                email: values.email,
                full_name: values.full_name || "",
                role: role,
                plant_hod: plant_hod || "",
                created_by: user?.email
            }

            await apiClient(ENDPOINTS.USER.ADD_USER, {
                method: 'POST',
                body: JSON.stringify(payload)
            })

            setAddUserModalOpen(false);
            addUserForm.resetFields();
            fetchUsers();
            setUserVersion(v => v + 1);
            message.success(MESSAGES.SUCCESS.ADD_USER);
        }
        catch (error) {
            message.error(error?.message || "Failed to add user");
        }
        finally {
            setUserSubmitLoading(false);
        }
    };

    const handleEquipUpdate = async (values) => {
        try {
            setEquipUpdateLoading(true);

            const payload = {
                ...buildEquipmentPayload(values),
                request_type: REQUEST_TYPES.UPDATE_EQUIPMENT,
                updated_by: userId
            }

            await apiClient(ENDPOINTS.EQUIPMENT.UPDATE_EQUIPMENT, {
                method: 'POST',
                body: JSON.stringify(payload)
            })

            setUpdateEquipModalOpen(false);
            updateEquipForm.resetFields();
            message.success(MESSAGES.SUCCESS.UPDATE_EQUIPMENT);
            fetchEquipments();
        }
        catch (error) {
            if (error.errorFields) {
                message.error("Please fill in all required fields.");
            } else {
                message.error(error?.message || "Failed to update equipment");
            }
        }
        finally {
            setEquipUpdateLoading(false);
        }
    };

    const handleUserUpdate = async () => {
        setUserUpdateLoading(true);
        try {
            const values = updateUserForm.getFieldValue();
            const payload = {
                request_type: REQUEST_TYPES.UPDATE_USER,
                email: values.email,
                full_name: values.full_name || "",
                role: values.role,
                plant_hod: values.plant_hod || ""
            }

            await apiClient(ENDPOINTS.USER.UPDATE_USER, {
                method: 'POST',
                body: JSON.stringify(payload)
            })

            setUpdateUserModalOpen(false);
            updateUserForm.resetFields();
            message.success(MESSAGES.SUCCESS.UPDATE_USER);
            fetchUsers();
            setUserVersion(v => v + 1);
        } catch (error) {
            if (error.errorFields) {
                message.error("Please fill all required field");
            } else {
                message.error(error?.message);
            }
        } finally {
            setUserUpdateLoading(false);
        }
    }

    const filteredAllEquipments = useMemo(() => {
        return equipments.filter((e) => {
            const matchType = allEquipsFilters.types.length ? allEquipsFilters.types.includes(e.equipment_type) : true;
            const matchVehicle = allEquipsFilters.vehicles.length ? allEquipsFilters.vehicles.includes(e.vehicle_no) : true;
            const matchOwner = allEquipsFilters.owners.length ? allEquipsFilters.owners.includes(e.owner_email) : true;
            const matchPlantUser = allEquipsFilters.plantUsers.length ? allEquipsFilters.plantUsers.includes(e.plant_user) : true;
            const matchPlantHod = allEquipsFilters.plantHods.length ? allEquipsFilters.plantHods.includes(e.plant_hod) : true;
            return matchType && matchVehicle && matchOwner && matchPlantUser && matchPlantHod;
        });
    }, [equipments, allEquipsFilters]);

    const allEquipsOptions = useMemo(() => {
        const types = Array.from(new Set(equipments.map(e => e.equipment_type).filter(Boolean)));
        const vehicles = Array.from(new Set(equipments.map(e => e.vehicle_no).filter(Boolean)));
        const owners = Array.from(new Set(equipments.map(e => e.owner_email).filter(Boolean)));
        const plantUsers = Array.from(new Set(equipments.map(e => e.plant_user).filter(Boolean)));
        const plantHods = Array.from(new Set(equipments.map(e => e.plant_hod).filter(Boolean)));

        return {
            types,
            vehicles,
            owners,
            plantUsers,
            plantHods
        };
    }, [equipments]);

    const filteredAllUsers = useMemo(() => {
        return users.filter((u) => {
            const matchEmail = allUsersFilters.email.length ? allUsersFilters.email.includes(u.email) : true;
            const matchRole = allUsersFilters.role.length ? allUsersFilters.role.includes(u.role) : true;
            const matchPlantHod = allUsersFilters.plantHods.length ? allUsersFilters.plantHods.includes(u.plant_hod) : true;
            return matchEmail && matchRole && matchPlantHod;
        });
    }, [users, allUsersFilters]);

    const allUsersOptions = useMemo(() => {
        const email = Array.from(new Set(users.map(u => u.email).filter(Boolean)));
        const role = Array.from(new Set(users.map(u => u.role).filter(Boolean)));
        const plantHods = Array.from(new Set(users.map(u => u.plant_hod).filter(Boolean)));

        return {
            email,
            role,
            plantHods
        };
    }, [users]);

    const equipmentColumns = [
        ...EQUIPMENT_BASE_COLUMNS.map(col => ({
            ...col,
            ellipsis: { showTitle: false },
            render: col.dataIndex === 'service_start_time' || col.dataIndex === 'service_end_time'
                ? (text) => <Tooltip placement="topLeft" title={text}>{text || 'N/A'}</Tooltip>
                : (text) => <Tooltip placement="topLeft" title={text}>{text}</Tooltip>,
        })),
        {
            title: 'Action',
            key: 'action',
            width: 'max-content',
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Button
                        type="link"
                        className="black-btn"
                        icon={<EyeOutlined />}
                        onClick={() => handleView(record)}
                        size="small"
                    >
                        View
                    </Button>
                    <Button
                        type="link"
                        className="black-btn"
                        icon={<EnvironmentOutlined />}
                        onClick={() => handleLiveLocation(record)}
                        loading={!!liveLocationLoading[record.vehicle_no]}
                        disabled={!record.vehicle_no}
                        size="small"
                    >
                        Live Location
                    </Button>
                    {[ROLES.SPEND_TEAM, ROLES.PLANT_USER, ROLES.PLANT_HOD].includes(userRole) && <Button
                        type="primary"
                        className="black-btn"
                        icon={<EditOutlined />}
                        onClick={() => handleUpdateEquipValue(record)}
                        size="small"
                    >
                        Update
                    </Button>
                    }
                </Space>
            ),
        },
    ];

    const userColumns = [
        ...USER_BASE_COLUMNS
            .filter(col => {
                if (col.key === 'full_name') return [ROLES.SPEND_TEAM, ROLES.ACCOUNT_TEAM, ROLES.PLANT_USER].includes(userRole);
                if (col.key === 'role') return [ROLES.SPEND_TEAM, ROLES.ACCOUNT_TEAM].includes(userRole);
                if (col.key === 'plant_hod') return [ROLES.SPEND_TEAM, ROLES.ACCOUNT_TEAM].includes(userRole);
                return true;
            })
            .map(col => ({
                ...col,
                ellipsis: { showTitle: false },
                render: (text) => <Tooltip placement="topLeft" title={text}>{text}</Tooltip>,
            })),
        ...(userRole == ROLES.SPEND_TEAM ? [{
            title: 'Action',
            key: 'action',
            width: 'max-content',
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        className="black-btn"
                        icon={<EditOutlined />}
                        onClick={() => handleUpdateUserValue(record)}
                        size="small"
                        disabled={record?.role == ROLES.SPEND_TEAM}
                    >
                        Update
                    </Button>
                </Space>
            ),
        }] : []),
    ];

    const tabItems = [
        {
            key: "all equipments",
            label: `All Equipments (${filteredAllEquipments.length})`,
            children: (
                <>
                    <EquipmentFilterBar
                        userRole={userRole}
                        options={allEquipsOptions}
                        filters={allEquipsFilters}
                        onFilterChange={setAllEquipsFilters}
                        onRefresh={fetchEquipments}
                        loading={equipLoading}
                        showAddEquipModal={showAddEquipModal}
                    />
                    <Table
                        columns={equipmentColumns}
                        dataSource={filteredAllEquipments}
                        rowKey="vehicle_no"
                        loading={equipLoading}
                        pagination={filteredAllEquipments.length > 10}
                        size="middle"
                        scroll={{ x: 'max-content' }}
                    />
                </>
            )
        },
        ...([ROLES.SPEND_TEAM, ROLES.PLANT_HOD, ROLES.PLANT_USER, ROLES.ACCOUNT_TEAM].includes(userRole) ? [{
            key: 'all users',
            label: [ROLES.SPEND_TEAM, ROLES.ACCOUNT_TEAM].includes(userRole) ? `All Users (${filteredAllUsers.length})` : userRole == ROLES.PLANT_HOD ? `All Plant Users (${filteredAllUsers.length})` : `All Owners (${filteredAllUsers.length})`,
            children: (
                <>
                    <UserFilterBar
                        userRole={userRole}
                        options={allUsersOptions}
                        filters={allUsersFilters}
                        onFilterChange={setAllUsersFilters}
                        onRefresh={fetchUsers}
                        loading={userLoading}
                        showAddUserModal={showAddUserModal}
                    />
                    <Table
                        columns={userColumns}
                        dataSource={filteredAllUsers}
                        rowKey="email"
                        loading={userLoading}
                        pagination={filteredAllUsers.length > 10}
                        size="middle"
                    />
                </>
            ),
        }] : [])
    ]

    useEffect(() => {
        fetchUsers();
        fetchEquipments();
    }, []);

    const resetAddEquipScroll = () => {
        requestAnimationFrame(() => {
            if (addEquipScrollRef.current) {
                addEquipScrollRef.current.scrollTop = 0;
            }
        });
    };

    const cardTitle = userRole === ROLES.OWNER
        ? <span>Equipment Management Dashboard <span className="text-[15px] font-normal text-gray-500">({filteredAllEquipments.length} {filteredAllEquipments.length === 1 ? 'Equipment' : 'Equipments'})</span></span>
        : "Resource Management Dashboard";

    return (
        <div>
            <Card title={<Title level={4} style={{ marginBottom: 0 }}>{cardTitle}</Title>}>
                {userRole == ROLES.OWNER ?
                    (
                        <>
                            <EquipmentFilterBar
                                userRole={userRole}
                                options={allEquipsOptions}
                                filters={allEquipsFilters}
                                onFilterChange={setAllEquipsFilters}
                                onRefresh={fetchEquipments}
                                loading={equipLoading}
                                showAddEquipModal={showAddEquipModal}
                            />
                            <Table
                                columns={equipmentColumns}
                                dataSource={filteredAllEquipments}
                                rowKey="vehicle_no"
                                loading={equipLoading}
                                pagination={filteredAllEquipments.length > 10}
                                size="middle"
                                scroll={{ x: 'max-content' }}
                            />
                        </>
                    ) : (
                        <Tabs
                            activeKey={activeTab}
                            onChange={setActiveTab}
                            items={tabItems}
                        />
                    )
                }
            </Card>

            <Modal
                title={<Title level={4} style={{ marginBottom: 0 }}>Add Equipment</Title>}
                open={addEquipModalOpen}
                afterOpenChange={(opened) => { if (opened) resetAddEquipScroll(); }}
                onCancel={() => {
                    addEquipForm.resetFields();
                    if (userRole === ROLES.PLANT_USER) {
                        addEquipForm.setFieldsValue({ plant_user: user?.email, plant_hod: user?.plant_hod });
                    }
                    setActiveDraftId(null);
                    setAddEquipModalOpen(false);
                }}
                footer={null}
                centered
                width={900}
                closeIcon={<span className="font-bold">✕</span>}
            >
                <Divider style={{ marginTop: 0 }} />
                <div ref={addEquipScrollRef} className="max-h-[80vh] overflow-y-auto overflow-x-hidden pr-2">
                    {drafts.length > 0 && (
                        <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                            <div className="text-sm font-semibold text-slate-600 mb-2">Saved Drafts</div>
                            <div className="flex flex-wrap gap-2">
                                {drafts.map(draft => (
                                    <Space key={draft.id} size={4}>
                                        <Button
                                            size="small"
                                            onClick={() => handleLoadDraft(draft.id)}
                                        >
                                            {draft.label} <span className="text-xs text-gray-400 ml-1">({draft.savedAt})</span>
                                        </Button>
                                        <Button
                                            size="small"
                                            danger
                                            type="text"
                                            onClick={() => { handleDeleteDraft(draft.id); }}
                                        >
                                            ✕
                                        </Button>
                                    </Space>
                                ))}
                            </div>
                        </div>
                    )}
                    <EquipmentForm
                        user={user}
                        isUpdate={false}
                        form={addEquipForm}
                        onSubmit={handleEquipSubmit}
                        loading={equipSubmitLoading}
                        userRole={userRole}
                        modalOpen={addEquipModalOpen}
                        userVersion={userVersion}
                        onSaveDraft={handleSaveEquipDraft}
                    />
                </div>
            </Modal>

            <Modal
                title={<Title level={4} style={{ fontWeight: "bold" }}>Equipment Details</Title>}
                open={viewModalOpen}
                onCancel={() => setViewModalOpen(false)}
                footer={<Button onClick={() => setViewModalOpen(false)}>Close</Button>}
                centered
                width={1200}
            >
                <Divider style={{ marginTop: 0 }} />
                <EquipmentDetailView data={selectedEquipment} />
            </Modal>

            <Modal
                title={<Title level={4} style={{ fontWeight: "bold" }}>Update Equipment</Title>}
                open={updateEquipModalOpen}
                onCancel={() => {
                    updateEquipForm.resetFields();
                    setUpdateEquipModalOpen(false);
                }}
                footer={null}
                centered
                width={900}
                closeIcon={<span className="font-bold">✕</span>}
            >
                <Divider style={{ marginTop: 0 }} />
                <div className="max-h-[80vh] overflow-y-auto overflow-x-hidden pr-2">
                    <EquipmentForm
                        user={user}
                        isUpdate={true}
                        form={updateEquipForm}
                        onSubmit={handleEquipUpdate}
                        loading={equipUpdateLoading}
                        userRole={userRole}
                        modalOpen={updateEquipModalOpen}
                        userVersion={userVersion}
                    />
                </div>
            </Modal>

            <Modal
                title={<Title level={4} style={{ marginBottom: 0 }}>{[ROLES.SPEND_TEAM, ROLES.ACCOUNT_TEAM].includes(userRole) ? "Add User" : userRole == ROLES.PLANT_HOD ? "Add Plant User" : "Add Owner"}</Title>}
                open={addUserModalOpen}
                onCancel={() => {
                    addUserForm.resetFields();
                    setAddUserModalOpen(false);
                }}
                footer={null}
                centered
                width={900}
                closeIcon={<span className="font-bold">✕</span>}
            >
                <Divider style={{ marginTop: 0 }} />
                <div className="max-h-[80vh] overflow-y-auto overflow-x-hidden pr-2">
                    <UserForm
                        user={user}
                        isUpdate={false}
                        form={addUserForm}
                        onSubmit={handleUserSubmit}
                        loading={userSubmitLoading}
                        userRole={userRole}
                        modalOpen={addUserModalOpen}
                    />
                </div>
            </Modal>

            <Modal
                title={<Title level={4} style={{ marginBottom: 0 }}>{[ROLES.SPEND_TEAM, ROLES.ACCOUNT_TEAM].includes(userRole) ? "Update User" : userRole == ROLES.PLANT_HOD ? "Update Plant User" : "Update Owner"}</Title>}
                open={updateUserModalOpen}
                onCancel={() => {
                    updateUserForm.resetFields();
                    setUpdateUserModalOpen(false);
                }}
                footer={null}
                centered
                width={900}
                closeIcon={<span className="font-bold">✕</span>}
            >
                <Divider style={{ marginTop: 0 }} />
                <div className="max-h-[80vh] overflow-y-auto overflow-x-hidden pr-2">
                    <UserForm
                        user={user}
                        isUpdate={true}
                        form={updateUserForm}
                        onSubmit={handleUserUpdate}
                        loading={userUpdateLoading}
                        userRole={userRole}
                        modalOpen={updateUserModalOpen}
                    />
                </div>
            </Modal>
        </div>
    );
}
